import { getExternalAuth } from "./firebase";

export class AdminApiError extends Error {
  constructor(code, status, requestId) {
    const messages = {
      stale_revision:
        "This record changed since you opened it. Reload the latest version before saving. Your draft is still here.",
      stale_default_revision: "This package’s defaults changed since you opened the selection. Your draft is kept. Reload the latest version before saving.",
      paperwork_content_unavailable: "Saved Paperwork content could not be read. Reload or contact the website operator.",
      bank_review_changed: "The reviewed instructions changed. Review the exact values again before applying.",
      bank_configuration_unavailable: "Saved bank instructions are unavailable. Reload and review the configuration.",
      invalid_bank_instructions: "Complete the required bank fields using valid, bounded text.",
      bank_credentials_forbidden: "Enter payment recipient details only, without login credentials or secrets.",
      revision_conflict: "This record changed. Cancel and reload the latest record before reviewing again.",
      partner_portal_not_eligible: "This partner or viewer is not eligible for that portal action. Refresh its current classification and access status.",
      membership_exists: "This customer already has a membership record for this partner.",
      customer_not_eligible: "Select an active, verified website customer.",
      partner_not_draft: "This partner is suspended. This action is unavailable.",
      forbidden: "Your account no longer has permission for this action.",
      delivery_file_not_ready: "Select validated private files belonging to this order.",
      delivery_lines_incomplete: "Select at least one prepared file for every purchased line before release.",
      delivery_lines_invalid: "Each selected file must belong to one purchased line in this order.",
      delivery_review_changed: "The order, customer or prepared files changed. Refresh and review again before release.",
      delivery_order_changed: "The purchased order scope changed. Refresh the order before preparing delivery.",
      delivery_payment_required: "This action requires a paid order with no payment review.",
      delivery_verified_customer_required: "The customer must sign in with the checkout email and claim the order before release.",
      delivery_already_released: "This order already has an immutable release. Refresh to see its current state.",
      delivery_file_unavailable: "A selected file is missing or could not be verified. Refresh before release.",
      admin_required: "Your admin session has ended. Sign in again.",
      invalid_credentials: "Your sign-in has expired. Sign in again.",
      invalid_input: "Check the required fields and formats before saving.",
      file_not_ready:
        "Select a file version that has completed validation and scanning.",
      preview_not_ready: "Select a scanned PDF version for the preview.",
      slug_conflict: "That URL slug is already in use. Choose another.",
      last_owner: "At least one active owner must remain.",
      self_role_change_forbidden: "Another owner must change your role.",
      invalid_assignee:
        "Choose an active staff member with access to this area.",
      rate_limited: "Too many requests. Wait a moment before trying again.",
      site_backend_disabled:
        "The website backend has not been activated. Contact the website operator.",
      network_error:
        "Connection interrupted. Retry the same action to check its result safely.",
      invalid_invitation:
        "This invitation has expired, was used, or belongs to another email address.",
      tms_contract_not_configured:
        "TMS is not connected. Website records remain saved.",
      reauthentication_required:
        "For your safety, confirm your sign-in again, then repeat the change.",
      settings_key_unavailable:
        "Secret settings are switched off on this server, so keys cannot be saved or used right now. Nothing was changed.",
      invalid_stripe_key:
        "One of the keys does not match its box or mode. Paste the publishable key, the secret key and the webhook signing secret from the same Stripe mode.",
      stripe_mode_not_ready:
        "These keys cannot be switched on yet. Save them and press Test connection until it says OK.",
      stripe_not_connected: "Paste the keys for this mode first.",
      test_mode_not_allowed: "The live website never takes payments with test keys. Use test keys on a local or staging copy.",
      invalid_booking_url: "Use the https link of a Cal.com booking page (cal.com or your cal.com subdomain).",
      invalid_webhook_secret: "The webhook secret must be 16 to 200 characters without spaces.",
      invalid_legal_kind: "This legal text does not exist.",
      legal_draft_missing: "Save a draft before publishing.",
      legal_nothing_to_publish: "This draft is the same as the published version. Change the text before publishing again.",
      invalid_capability: "That right cannot be switched on for one person.",
      staff_not_found: "This staff member no longer exists. Reload the team list.",
      invalid_sale_price: "A sale price must be lower than the original price, and a sale can only be on when a sale price is set.",
      addon_tier_not_allowed: "Specialist modules and extra documents are offered to Standard and Premium buyers only.",
      prices_unavailable: "The saved prices could not be read. Open Paperwork prices and save them again.",
      edition_not_found: "This edition does not exist. Choose a package, truck, arrangement and state from the list.",
      edition_text_required: "Write what this edition covers before publishing it.",
      edition_version_not_checked: "This file has not passed the edition check. Upload a file that passes, then publish.",
      edition_version_mismatch: "This file was uploaded for another edition.",
      edition_review_changed: "The file changed since you opened this page. Reload and check it again before publishing.",
      edition_not_published: "This edition is not published.",
      edition_publication_changed: "The published file changed since you opened this order. Refresh and prepare again.",
      edition_policy_changed: "The edition rules changed since this file was published. Publish it again before preparing orders.",
      edition_file_unavailable: "The published file could not be read safely. Publish the edition again or upload this order's file.",
      edition_line_mismatch: "That edition does not match this order line.",
      edition_both_manual: "Orders for both arrangements are prepared by hand. Upload this order's file below.",
      edition_quantity_manual: "Quantities above 1 are prepared by hand. Upload this order's file below.",
      edition_tier_manual: "Standard and Premium are prepared by hand. Upload this order's file below.",
      delivery_edition_line_mismatch: "An edition file sits on a line it was not prepared for. Prepare that line again.",
      delivery_edition_superseded: "The edition changed after this order was prepared. Prepare the line again.",
      delivery_edition_mismatch: "The prepared file does not match the published edition. Prepare the line again.",
      paperwork_catalogue_retired: "The old product catalogue is retired. Use Paperwork editions.",
      upload_integrity_failed: "The uploaded file did not arrive complete. Upload it again.",
      delivery_checks_required: "Tick the DOT, MC, email and money-received checks before releasing this order.",
      deposit_refund_window_open: "The 24-hour refund window is still open. Grant the Basic edition after it ends.",
      deposit_already_settled: "This deposit is already settled: the buyer continued, or the Basic edition was granted.",
      deposit_credit_expired: "The 30-day credit for this deposit has ended.",
      addon_not_available: "That add-on is not offered for this package. Check Paperwork prices.",
      upgrade_credit_invalid: "That order cannot be credited: it must be the same buyer's Basic order for the same truck and arrangement, paid within 30 days and not credited before.",
      nothing_to_pay: "The credits cover the whole price, so there is nothing left to pay.",
      delivery_order_invalid: "This order is not delivered from this screen.",
      catch_up_count_changed: "The number of queued items changed. Check the new count and send again.",
      link_off: "The TMS link is off. Queued items wait until it is on.",
      edition_customized_manual: "Customized lines are prepared by hand. Upload this order's file below.",
    };
    super(
      messages[code] ||
        `Unable to complete the request: ${String(code).replace(/_/g, " ")}.`,
    );
    this.code = code;
    this.status = status;
    this.requestId = requestId;
  }
}
export const operationKey = () => crypto.randomUUID();
export function queryString(params = {}) {
  return new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== "" && v != null),
  ).toString();
}
export async function adminRequest(
  path,
  {
    method = "GET",
    body,
    key,
    signal,
    binary = false,
    partChecksum,
    blob = false,
    user,
  } = {},
) {
  if (!path.startsWith("/admin") || path.includes("://"))
    throw new Error("Invalid admin path");
  const identity = user || getExternalAuth()?.currentUser;
  if (!identity) throw new AdminApiError("admin_required", 401);
  let token;
  try {
    token = await identity.getIdToken();
  } catch {
    throw new AdminApiError("invalid_credentials", 401);
  }
  const headers = { Authorization: `Bearer ${token}` };
  if (partChecksum !== undefined) {
    if (!binary || !/^[a-f0-9]{64}$/.test(partChecksum))
      throw new Error("Invalid part checksum");
    headers["X-Part-Checksum"] = partChecksum;
  }
  if (method !== "GET") {
    headers["Content-Type"] = binary
      ? "application/octet-stream"
      : "application/json";
    headers["Idempotency-Key"] = key || operationKey();
  }
  let response;
  try {
    response = await fetch(`/api/site/v1${path}`, {
      method,
      headers,
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      signal,
      body:
        body === undefined ? undefined : binary ? body : JSON.stringify(body),
    });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw new AdminApiError("network_error", 0);
  }
  if (response.ok && blob)
    return {
      data: await response.blob(),
      requestId: response.headers.get("x-request-id"),
    };
  let result;
  try {
    result = await response.json();
  } catch {
    throw new AdminApiError("service_unavailable", 503);
  }
  if (!response.ok) {
    const error = new AdminApiError(
      result.error?.code || "service_unavailable",
      response.status,
      result.requestId,
    );
    if (path !== "/admin/me" && [401, 403].includes(response.status))
      window.dispatchEvent(new Event("magellan-admin-recheck"));
    throw error;
  }
  return result;
}
// Website Link v1 admin reads (docs/TMS_CONNECTION_PLAN_2026-10-06.md section 11, D10, D20).
export const linkPaths = {
  status: () => "/admin/link/status",
  sync: (params = {}) => `/admin/sync?${queryString(params)}`,
  entity: (type, id) => `/admin/link/entity?${queryString({ type, id })}`,
  refundable: (orderId) =>
    `/admin/commerce/orders/${encodeURIComponent(orderId)}/refundable`,
};
// A reviewed sync retry. While the link is off the server answers "deferred" and changes
// nothing, so that answer is reported as an error instead of a saved retry.
export async function retrySyncJob(id, body, key) {
  const result = await adminRequest(
    `/admin/jobs/sync/${encodeURIComponent(id)}/retry`,
    { method: "POST", body, key },
  );
  if (result?.data?.status === "deferred")
    throw new AdminApiError(
      result.data.code || "tms_contract_not_configured",
      503,
      result.requestId,
    );
  return result;
}
export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
export async function hashFile(file, onProgress = () => {}, signal) {
  const { sha256 } = await import("@noble/hashes/sha2.js");
  const hash = sha256.create();
  for (let offset = 0; offset < file.size; offset += 4 * 1024 * 1024) {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    hash.update(
      new Uint8Array(
        await file.slice(offset, offset + 4 * 1024 * 1024).arrayBuffer(),
      ),
    );
    onProgress(Math.min(file.size, offset + 4 * 1024 * 1024) / file.size);
  }
  return Array.from(hash.digest(), (n) => n.toString(16).padStart(2, "0")).join(
    "",
  );
}
